import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Bike, Car, HelpCircle, Home } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { apiFetch, getToken } from "@/lib/api";
import { type OtherSubPersona, type PersonaId, useOnboardingStore } from "@/stores/onboarding";
import { cn } from "@/lib/utils";
import { trackFunnel } from "@/lib/analytics";

export const Route = createFileRoute("/onboarding/persona")({
  component: PersonaStep,
});

const PERSONAS: Array<{ id: PersonaId; icon: typeof Car; benefit: string }> = [
  { id: "driver", icon: Car, benefit: "Route risk checks and Drive Mode" },
  { id: "rider", icon: Bike, benefit: "Ride now/wait advice and night-ride alerts" },
  { id: "resident", icon: Home, benefit: "Home risk, checklists and family alerts" },
  { id: "other", icon: HelpCircle, benefit: "Tools matched to your role" },
];

const OTHER_SUBS: OtherSubPersona[] = [
  "commuter",
  "parent",
  "farmer",
  "fisher",
  "business",
  "community",
  "traveller",
  "fleet",
  "responder",
];

function PersonaStep() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const personas = useOnboardingStore((s) => s.personas);
  const otherSub = useOnboardingStore((s) => s.otherSub);
  const togglePersona = useOnboardingStore((s) => s.togglePersona);
  const setOtherSub = useOnboardingStore((s) => s.setOtherSub);
  const setPersonas = useOnboardingStore((s) => s.setPersonas);
  const [showOther, setShowOther] = useState(personas.includes("other"));

  function select(id: PersonaId) {
    if (id === "other") {
      setShowOther(true);
      if (!personas.includes("other")) togglePersona("other");
      return;
    }
    togglePersona(id);
  }

  function notSure() {
    setPersonas(["resident"]);
    setOtherSub(null);
    trackFunnel("step_completed", "persona");
    void savePersonas(["resident"]).finally(() => {
      void navigate({ to: "/onboarding/alerts" });
    });
  }

  async function savePersonas(next: PersonaId[]) {
    if (!getToken("user")) return;
    await apiFetch("/user/onboarding", {
      method: "PATCH",
      audience: "user",
      body: JSON.stringify({
        step: "alerts",
        personas: { personas: next, ...(otherSub ? { otherSub } : {}) },
      }),
    }).catch(() => undefined);
  }

  function continueNext() {
    const next = personas.length === 0 ? (["resident"] as PersonaId[]) : personas;
    if (personas.length === 0) setPersonas(next);
    if (next.includes("other") && !otherSub) return;
    trackFunnel("step_completed", "persona");
    void savePersonas(next).finally(() => {
      void navigate({ to: "/onboarding/alerts" });
    });
  }

  return (
    <div className="flex flex-1 flex-col">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-primary">
        {t("onboarding.persona.eyebrow", "About you")}
      </p>
      <h1 className="font-display text-3xl font-bold">
        {t("onboarding.persona.title", "How will you use this app?")}
      </h1>
      <p className="mt-3 text-sm text-muted-foreground">
        {t("onboarding.persona.body", "Pick one or more. You can change this later.")}
      </p>

      <div className="mt-6 grid gap-3">
        {PERSONAS.map(({ id, icon: Icon, benefit }) => (
          <button
            key={id}
            type="button"
            onClick={() => select(id)}
            className={cn(
              "flex min-h-16 items-start gap-3 rounded-lg border px-4 py-3 text-left transition-colors",
              personas.includes(id) ? "border-primary bg-primary-soft" : "border-border bg-card",
            )}
          >
            <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <span>
              <span className="block text-sm font-bold capitalize">
                {id === "resident" ? "Everyday resident" : id}
              </span>
              <span className="mt-1 block text-xs text-muted-foreground">{benefit}</span>
            </span>
          </button>
        ))}
      </div>

      {showOther || personas.includes("other") ? (
        <div className="mt-4 grid grid-cols-2 gap-2">
          {OTHER_SUBS.map((sub) => (
            <button
              key={sub}
              type="button"
              onClick={() => setOtherSub(sub)}
              className={cn(
                "rounded-md border px-3 py-2 text-left text-xs font-semibold capitalize",
                otherSub === sub ? "border-primary bg-primary-soft" : "border-border",
              )}
            >
              {sub}
            </button>
          ))}
        </div>
      ) : null}

      {personas.length > 0 ? (
        <p className="mt-4 rounded-md bg-muted p-3 text-xs text-muted-foreground">
          You&apos;ll get:{" "}
          {personas.includes("driver")
            ? "route risk checks and Drive Mode"
            : personas.includes("rider")
              ? "ride advice and stage alerts"
              : "place risk cards and household tools"}
          .
        </p>
      ) : null}

      <div className="mt-auto space-y-2 pt-8">
        <Button
          className="w-full"
          disabled={personas.includes("other") && !otherSub}
          onClick={continueNext}
        >
          Continue
        </Button>
        <Button variant="ghost" className="w-full" onClick={notSure}>
          I&apos;m not sure — use Everyday resident
        </Button>
        <Button
          variant="outline"
          className="w-full"
          onClick={() => {
            trackFunnel("step_skipped", "persona");
            void navigate({ to: "/onboarding/welcome" });
          }}
        >
          Back
        </Button>
      </div>
    </div>
  );
}
